// Chemin: C:\gestion-copro\src\pages\admin-saas\ConfigPortailPage.tsx
// Configuration du portail d'entrée d'une copropriété
// Session 54 : Création initiale

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  Eye,
  Building2,
  Palette,
  FileText,
  Link as LinkIcon,
  Check,
  AlertTriangle,
  Image
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface CoproprieteConfig {
  id: string;
  nom: string;
  adresse?: string;
  ville?: string;
  codePostal?: string;
  slug?: string;
  sceauUrl?: string;
  presentation?: string;
  couleurPrimaire?: string;
}

// ============================================
// COMPOSANT
// ============================================

const ConfigPortailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [copropriete, setCopropriete] = useState<CoproprieteConfig | null>(null);
  const [slug, setSlug] = useState('');
  const [presentation, setPresentation] = useState('');
  const [couleurPrimaire, setCouleurPrimaire] = useState('#1E3A8A');
  const [sceauUrl, setSceauUrl] = useState('');
  const [sceauPreview, setSceauPreview] = useState<string | null>(null);

  // Charger la copropriété
  useEffect(() => {
    const loadCopropriete = async () => {
      if (!id) {
        setError('ID copropriété manquant');
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'coproprietes', id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError('Copropriété introuvable');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        setCopropriete({
          id: docSnap.id,
          nom: data.nom || '',
          adresse: data.adresse,
          ville: data.ville,
          codePostal: data.codePostal,
          slug: data.slug,
          sceauUrl: data.sceauUrl,
          presentation: data.presentation,
          couleurPrimaire: data.couleurPrimaire
        });

        // Pré-remplir les champs
        setSlug(data.slug || '');
        setPresentation(data.presentation || '');
        setCouleurPrimaire(data.couleurPrimaire || '#1E3A8A');
        setSceauUrl(data.sceauUrl || '');

      } catch (err) {
        console.error('Erreur chargement:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    loadCopropriete();
  }, [id]);

  // Générer un slug à partir du nom
  const generateSlug = () => {
    if (!copropriete?.nom) return;
    const generated = copropriete.nom
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^a-z0-9]+/g, '-')     // Remplace les caractères spéciaux par -
      .replace(/^-+|-+$/g, '');        // Supprime les - en début/fin
    setSlug(generated);
  };

  // Upload du sceau
  const handleSceauUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image');
      return;
    }

    // Vérifier la taille (max 2Mo)
    if (file.size > 2 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Preview local
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSceauPreview(ev.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload vers Firebase Storage
      const storageRef = ref(storage, `coproprietes/${id}/sceau.png`);
      await uploadBytes(storageRef, file);
      
      // Récupérer l'URL
      const url = await getDownloadURL(storageRef);
      setSceauUrl(url);

    } catch (err) {
      console.error('Erreur upload:', err);
      setError('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  };

  // Sauvegarder
  const handleSave = async () => {
    if (!id) return;

    // Validation
    if (!slug.trim()) {
      setError('Le slug est obligatoire');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      setError('Le slug ne doit contenir que des lettres minuscules, chiffres et tirets');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const docRef = doc(db, 'coproprietes', id);
      await updateDoc(docRef, {
        slug: slug.toLowerCase(),
        presentation: presentation.trim() || null,
        couleurPrimaire: couleurPrimaire,
        sceauUrl: sceauUrl || null,
        updatedAt: new Date()
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error && !copropriete) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
        <p className="text-gray-700">{error}</p>
        <Link
          to="/admin-saas/coproprietes"
          className="mt-4 inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
        >
          <ArrowLeft size={16} />
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin-saas/coproprietes"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Configuration du portail
          </h1>
          <p className="text-gray-500">
            {copropriete?.nom}
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="text-red-500" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
          <Check className="text-emerald-500" size={20} />
          <p className="text-emerald-700">Configuration enregistrée avec succès</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Building2 size={20} className="text-gray-400" />
            Paramètres du portail
          </h2>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <LinkIcon size={14} className="inline mr-1" />
              URL du portail (slug)
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  copro-protect.fr/
                </span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  className="w-full pl-32 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="orion"
                />
              </div>
              <button
                type="button"
                onClick={generateSlug}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Générer
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Lettres minuscules, chiffres et tirets uniquement
            </p>
          </div>

          {/* Présentation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText size={14} className="inline mr-1" />
              Texte de présentation (optionnel)
            </label>
            <textarea
              value={presentation}
              onChange={(e) => setPresentation(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Bienvenue sur l'espace du Conseil Syndical de la Résidence..."
            />
          </div>

          {/* Couleur primaire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Palette size={14} className="inline mr-1" />
              Couleur primaire
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={couleurPrimaire}
                onChange={(e) => setCouleurPrimaire(e.target.value)}
                className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
              />
              <input
                type="text"
                value={couleurPrimaire}
                onChange={(e) => setCouleurPrimaire(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="#1E3A8A"
              />
            </div>
          </div>

          {/* Sceau */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Image size={14} className="inline mr-1" />
              Sceau / Logo du Conseil Syndical
            </label>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleSceauUpload}
              className="hidden"
            />

            <div className="flex items-start gap-4">
              {/* Aperçu */}
              <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                {sceauPreview || sceauUrl ? (
                  <img
                    src={sceauPreview || sceauUrl}
                    alt="Sceau"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Image className="text-gray-300" size={32} />
                )}
              </div>

              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Choisir une image
                    </>
                  )}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  PNG ou JPG, 2 Mo max. Fond transparent recommandé.
                </p>
              </div>
            </div>
          </div>

          {/* Bouton sauvegarder */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 font-medium"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={18} />
                Enregistrer la configuration
              </>
            )}
          </button>
        </div>

        {/* Aperçu */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Eye size={20} className="text-gray-400" />
            Aperçu du portail
          </h2>

          {/* Mini aperçu du portail */}
          <div 
            className="rounded-xl overflow-hidden border border-gray-200"
            style={{ backgroundColor: '#f9fafb' }}
          >
            <div className="p-8 text-center">
              {/* Sceau */}
              <div className="mb-4 flex justify-center">
                {sceauPreview || sceauUrl ? (
                  <img
                    src={sceauPreview || sceauUrl}
                    alt="Sceau"
                    className="w-20 h-20 object-contain"
                  />
                ) : (
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: couleurPrimaire }}
                  >
                    <Building2 className="text-white" size={32} />
                  </div>
                )}
              </div>

              {/* Nom */}
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {copropriete?.nom}
              </h3>

              {/* Adresse */}
              {copropriete?.ville && (
                <p className="text-gray-500 text-sm mb-4">
                  {copropriete.adresse}, {copropriete.ville}
                </p>
              )}

              {/* Présentation */}
              {presentation && (
                <p className="text-gray-600 text-sm mb-4 max-w-xs mx-auto">
                  {presentation}
                </p>
              )}

              {/* Bouton */}
              <button
                className="px-6 py-3 rounded-lg text-white font-medium"
                style={{ backgroundColor: couleurPrimaire }}
              >
                Accéder à l'espace
              </button>
            </div>
          </div>

          {/* URL */}
          {slug && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">URL du portail :</p>
              <a
                href={`/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 font-mono text-sm"
              >
                copro-protect.fr/{slug}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigPortailPage;
